import {
  Box,
  Stack,
  Alert,
  StackSeparator,
  HStack,
  ButtonGroup,
  Text,
  Button,
  Image,
  Input,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import { StreetViewComponent } from "./StreetViewComponent";
import { useRef, useState } from "react";
import turf from "../../../shared/utils/turf";
import { splitLineAtDistances } from "../../../map/utils/map-utils";
import { toast } from "../../../shared/views/CustomToaster";
import { PhotoSource } from "../../../cra/models/CraNode";
import { EditIcon } from "../../../shared/views/icons";

const HlaSimulationPhotoViewBase = () => {
  const { hlaStore, streetViewStore, simulationStore, mapStore } = useStores();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hlaWay = hlaStore.getSelected()!;
  const data = streetViewStore.distanceToSelectedHla;
  const simulationWay = simulationStore.selectedSimulationWay;
  const savedPhotoUrl = simulationWay?.photo?.url;

  const displayedPhotoUrl = photoUrl ?? savedPhotoUrl;

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1] ?? result;
        resolve(base64);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotoUrl(URL.createObjectURL(file));
    setUploading(true);

    try {
      const base64Image = await fileToBase64(file);

      simulationStore.code((prev) => ({
        ...prev,
        photo: {
          image: base64Image,
          source: PhotoSource.HlaSimulation,
        },
      }));
    } catch {
      toast("error", "Could not read photo file");
      setPhotoUrl(undefined);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleSplit = () => {
    const lineString = turf.lineString(hlaWay.coordinates);

    const newLines = splitLineAtDistances(lineString, [
      data!.distanceAlongLine,
    ]).map((feature) =>
      feature.geometry.coordinates.map(
        (point) =>
          ({
            lat: point[1],
            lng: point[0],
          } as mapboxgl.LngLat),
      ),
    );

    setLoading(true);

    hlaStore
      .cutSelectedWay(newLines)
      .then((ways) => {
        ways && hlaStore.select(ways[1].id);
        toast("success", "Split successful");
      })
      .catch(() => {
        toast("error", "Split failed");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!hlaWay) return null;

  return (
    <Stack height="50%" separator={<StackSeparator />} gap={0}>
      {!simulationWay && (
        <HStack fontSize="sm" p={2}>
          <Text flexGrow={1}>
            {data?.distanceAlongLine.toFixed(2)} meters from the start
          </Text>

          <ButtonGroup size="sm" colorPalette="primary" variant="surface">
            <Button
              onClick={handleSplit}
              loading={loading}
              disabled={
                !data ||
                lowPrecisionEquals(data.distanceAlongLine, 0) ||
                lowPrecisionEquals(data.distanceAlongLine, hlaWay.lengthM)
              }
            >
              Split here
            </Button>
          </ButtonGroup>
        </HStack>
      )}

      <Box flexGrow={1} maxHeight="100%" flexShrink={0} position="relative">
        {simulationStore.isEditing && simulationWay ? (
          <Box h="100%" w="100%" bg="gray.50">
            <Input
              ref={inputRef}
              type="file"
              accept="image/*"
              display="none"
              onChange={handlePhotoChange}
            />

            {displayedPhotoUrl ? (
              <Box h="100%" w="100%" position="relative">
                <Image
                  src={displayedPhotoUrl}
                  alt="Simulation reference"
                  h="100%"
                  w="100%"
                  objectFit="contain"
                />

                <IconButton
                  aria-label="Change photo"
                  position="absolute"
                  top={3}
                  right={3}
                  size="sm"
                  colorPalette="primary"
                  variant="solid"
                  loading={uploading}
                  onClick={() => inputRef.current?.click()}
                >
                  <Icon as={EditIcon} />
                </IconButton>
              </Box>
            ) : (
              <Stack h="100%" w="100%" align="center" justify="center" gap={4}>
                <Text fontWeight="semibold">Add a reference photo</Text>

                <Button
                  colorPalette="primary"
                  loading={uploading}
                  onClick={() => inputRef.current?.click()}
                >
                  Upload photo
                </Button>
              </Stack>
            )}
          </Box>
        ) : (
          <>
            <StreetViewComponent
              streetViewMapSource={mapStore.streetViewMapSource}
            />

            {!!data && (
              <Stack
                position="absolute"
                top={3}
                left={3}
                zIndex={1}
                alignItems="start"
              >
                {!data.currentPositionIsNear && (
                  <Alert.Root size="sm" w="340px" status="warning">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Description>
                        You moved away from the selected segment.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                )}

                {!data.currentPositionIsNear && !!data.otherWayNearbyId && (
                  <Button
                    size="sm"
                    fontSize="sm"
                    variant="surface"
                    colorPalette="primary"
                    onClick={() => {
                      hlaStore.select(data.otherWayNearbyId!);
                      streetViewStore.calcDistanceToSelectedHla();
                    }}
                  >
                    Select nearest segment
                  </Button>
                )}

                {!!data?.currentPosition &&
                  !data?.otherWayNearbyId &&
                  !data?.currentPositionIsNear && (
                    <Button
                      size="sm"
                      fontSize="sm"
                      variant="surface"
                      colorPalette="primary"
                      onClick={() => {
                        streetViewStore.queryCurrentPosition();
                      }}
                    >
                      Create new segment here
                    </Button>
                  )}
              </Stack>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

export const HlaSimulationPhotoView = observer(HlaSimulationPhotoViewBase);

const lowPrecisionEquals = (n1: number, n2: number, decimals = 1) => {
  return n1.toFixed(decimals) === n2.toFixed(decimals);
};
